/**
 * StatsDrawer — personal stats summary card + league leaderboard.
 * Theme-aware: uses Tailwind dark: variants throughout.
 * Stats are pre-formatted strings from useBottomNavData (ppg/rpg/apg as toFixed(1)).
 * Leaderboard rows show team logo/color swatch next to each player.
 * Logged-out users see a sign-in prompt instead of personal stats.
 * "You" label is only shown for authenticated users.
 */
import React from "react";
import { motion } from "motion/react";
import {
  BarChart3,
  TrendingUp,
  UserCircle2,
  ChevronRight,
  Medal,
  Settings2,
  LogIn,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DrawerShell from "./DrawerShell";

// ── Stat pill ─────────────────────────────────────────────────────────────────
const StatPill = ({ label, value, accent }) => (
  <div className="flex flex-col items-center gap-0.5">
    <span
      className={`text-xl font-black tabular-nums ${
        accent
          ? "text-orange-500 dark:text-orange-400"
          : "text-gray-900 dark:text-white"
      }`}
    >
      {value ?? "—"}
    </span>
    <span className="text-[9px] font-black tracking-[0.15em] uppercase text-gray-400 dark:text-gray-500">
      {label}
    </span>
  </div>
);

// ── Team logo / color dot for leaderboard ─────────────────────────────────────
const TeamDot = ({ team }) => {
  if (!team) {
    return (
      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
    );
  }
  if (team.logo_url) {
    return (
      <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <img
          src={team.logo_url}
          alt={team.name}
          className="w-full h-full object-contain"
        />
      </div>
    );
  }
  // Fallback: primary color dot
  const color = team.primary_color
    ? team.primary_color.startsWith("#")
      ? team.primary_color
      : `#${team.primary_color}`
    : "#f97316";
  return (
    <div
      className="w-5 h-5 rounded-full flex-shrink-0 border border-white/20"
      style={{ backgroundColor: color }}
      title={team.name}
    />
  );
};

// ── Personal stats card ───────────────────────────────────────────────────────
const PersonalStatsCard = ({
  stats,
  lastGame,
  userName,
  isLoggedIn,
  onLogin,
}) => {
  // Logged-out state
  if (!isLoggedIn) {
    return (
      <div
        className="mx-5 mt-4 rounded-2xl
                      bg-gray-50 dark:bg-gray-900
                      border border-gray-200 dark:border-white/[0.07]
                      p-5 text-center"
      >
        <LogIn
          size={28}
          className="text-orange-400 dark:text-orange-500 mx-auto mb-2"
        />
        <p className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-0.5">
          Sign in to see your stats
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-600 mb-4">
          Your season stats, PPG, RPG, and more appear here.
        </p>
        <button
          onClick={onLogin}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg
                     bg-orange-500 hover:bg-orange-600
                     text-white text-xs font-bold
                     transition-colors active:scale-95"
        >
          <LogIn size={13} />
          Log In
        </button>
      </div>
    );
  }

  // Logged in but no stats yet
  if (!stats) {
    return (
      <div
        className="mx-5 mt-4 rounded-2xl
                      bg-gray-50 dark:bg-gray-900
                      border border-gray-200 dark:border-white/[0.07]
                      p-5 text-center"
      >
        <BarChart3
          size={28}
          className="text-gray-300 dark:text-gray-700 mx-auto mb-2"
        />
        <p className="text-sm text-gray-500 dark:text-gray-500 font-medium">
          No stats yet this season.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-600 mt-0.5">
          Play some games to see your numbers.
        </p>
      </div>
    );
  }

  // Values from hook are already toFixed(1) strings or raw numbers
  const fmt = (v) => (v != null ? String(v) : "—");

  return (
    <div
      className="mx-5 mt-4 rounded-2xl
                    bg-orange-50 dark:bg-orange-500/10
                    border border-orange-200 dark:border-orange-500/20
                    p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-black tracking-[0.18em] uppercase text-orange-600 dark:text-orange-400">
            {stats.source === "career" ? "Career Stats" : "Season Stats"}
          </p>
          {userName && (
            <p className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">
              {userName}
            </p>
          )}
        </div>
        <div
          className="flex items-center gap-1.5
                        bg-orange-100 dark:bg-orange-500/20
                        px-2.5 py-1 rounded-full"
        >
          <TrendingUp
            size={11}
            className="text-orange-600 dark:text-orange-400"
          />
          <span className="text-xs font-bold text-orange-700 dark:text-orange-300">
            {stats.games_played ?? 0} GP
          </span>
        </div>
      </div>

      {/* Primary season averages */}
      <div
        className="grid grid-cols-3 gap-2
                      bg-white/60 dark:bg-black/20
                      rounded-xl p-3 mb-3"
      >
        <StatPill label="PPG" value={fmt(stats.ppg)} accent />
        <StatPill label="RPG" value={fmt(stats.rpg)} />
        <StatPill label="APG" value={fmt(stats.apg)} />
      </div>

      {/* Last game row — replaces SPG/BPG/FG%/PTS */}
      {lastGame ? (
        <div className="rounded-xl bg-white/60 dark:bg-black/20 px-3 py-2.5">
          <p className="text-[9px] font-black tracking-[0.15em] uppercase text-orange-500 dark:text-orange-400 mb-1.5">
            Last Game{lastGame.dateStr ? ` · ${lastGame.dateStr}` : ""}
          </p>
          <div className="grid grid-cols-3 gap-2">
            <StatPill label="PTS" value={lastGame.pts ?? "—"} accent />
            <StatPill label="REB" value={lastGame.reb ?? "—"} />
            <StatPill label="AST" value={lastGame.ast ?? "—"} />
          </div>
        </div>
      ) : (
        <div className="rounded-xl bg-white/60 dark:bg-black/20 px-3 py-2.5 text-center">
          <p className="text-[10px] text-gray-400 dark:text-gray-600">
            No recent game data
          </p>
        </div>
      )}
    </div>
  );
};

// ── Rank medal color ──────────────────────────────────────────────────────────
const rankColor = (rank) => {
  if (rank === 1) return "text-yellow-500";
  if (rank === 2) return "text-gray-400";
  if (rank === 3) return "text-amber-600";
  return "text-gray-300 dark:text-gray-600";
};

// ── Leaderboard row ───────────────────────────────────────────────────────────
const LeaderRow = ({ entry, rank, index, currentUserId, isLoggedIn }) => {
  // Only highlight "You" for authenticated users
  const isMe =
    isLoggedIn && !!currentUserId && entry.profile_id === currentUserId;
  // ppg/rpg/apg are raw floats from the hook aggregation
  const fmt = (v) => (v != null ? Number(v).toFixed(1) : "—");
  // name comes directly from players.name — no nested profile join
  const displayName = entry.name || "Player";

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: index * 0.04,
        type: "spring",
        stiffness: 380,
        damping: 32,
      }}
      className={`flex items-center gap-3 px-5 py-2.5 ${
        isMe
          ? "bg-orange-50 dark:bg-orange-500/10 border-l-2 border-orange-500"
          : ""
      }`}
    >
      {/* Rank */}
      <div className="w-7 flex-shrink-0 flex items-center justify-center">
        {rank <= 3 ? (
          <Medal size={16} className={rankColor(rank)} />
        ) : (
          <span className="text-xs font-black text-gray-400 dark:text-gray-600 tabular-nums">
            {rank}
          </span>
        )}
      </div>

      {/* Team logo dot */}
      <TeamDot team={entry.team} />

      {/* Jersey number or avatar placeholder */}
      <div
        className="w-8 h-8 rounded-full
                      bg-gray-100 dark:bg-gray-800
                      border border-gray-200 dark:border-gray-700
                      overflow-hidden flex-shrink-0 flex items-center justify-center"
      >
        {entry.jersey_number != null ? (
          <span className="text-[10px] font-black text-gray-500 dark:text-gray-400">
            #{entry.jersey_number}
          </span>
        ) : (
          <UserCircle2 size={18} className="text-gray-400 dark:text-gray-600" />
        )}
      </div>

      {/* Name */}
      <span
        className={`flex-1 text-sm font-semibold truncate ${
          isMe
            ? "text-orange-600 dark:text-orange-300"
            : "text-gray-700 dark:text-gray-300"
        }`}
      >
        {displayName}
        {isMe && (
          <span className="ml-1.5 text-[9px] font-black tracking-wide text-orange-500 uppercase">
            You
          </span>
        )}
      </span>

      {/* Stats */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="text-right">
          <p className="text-sm font-black text-gray-900 dark:text-white tabular-nums">
            {fmt(entry.ppg)}
          </p>
          <p className="text-[9px] text-gray-400 dark:text-gray-600 uppercase tracking-wide">
            PPG
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 tabular-nums">
            {fmt(entry.rpg)}
          </p>
          <p className="text-[9px] text-gray-400 dark:text-gray-600 uppercase tracking-wide">
            RPG
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 tabular-nums">
            {fmt(entry.apg)}
          </p>
          <p className="text-[9px] text-gray-400 dark:text-gray-600 uppercase tracking-wide">
            APG
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const StatsDrawer = ({
  open,
  onClose,
  myStats,
  myLastGame,
  leaderboard,
  currentUserId,
  userName,
  isAdmin,
  isLoggedIn,
}) => {
  const navigate = useNavigate();

  const handleNav = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      title="Stats"
      subtitle="Season leaderboard"
    >
      <div className="pb-4">
        {/* Admin shortcut */}
        {isAdmin && (
          <div className="mx-5 mt-4 mb-1">
            <button
              onClick={() => handleNav("/admin?tab=adjustments")}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl
                         bg-red-50 dark:bg-red-500/10
                         border border-red-200 dark:border-red-500/25
                         hover:bg-red-100 dark:hover:bg-red-500/15
                         transition-all group"
            >
              <Settings2
                size={15}
                className="text-red-500 dark:text-red-400 flex-shrink-0"
              />
              <span className="text-sm font-bold text-red-700 dark:text-red-300 group-hover:text-red-800 dark:group-hover:text-red-200 transition-colors">
                Stat Adjustments
              </span>
              <ChevronRight
                size={14}
                className="text-red-300 dark:text-red-600 ml-auto group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
              />
            </button>
          </div>
        )}

        {/* Personal stats */}
        <PersonalStatsCard
          stats={myStats}
          lastGame={myLastGame}
          userName={userName}
          isLoggedIn={isLoggedIn}
          onLogin={() => handleNav("/login")}
        />

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <>
            <p className="mx-5 mt-5 mb-1 text-[10px] font-black tracking-[0.18em] uppercase text-gray-400 dark:text-gray-500">
              League Leaders — PPG
            </p>
            <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
              {leaderboard.map((entry, i) => (
                <LeaderRow
                  key={entry.player_id ?? entry.profile_id ?? i}
                  entry={entry}
                  rank={i + 1}
                  index={i}
                  currentUserId={currentUserId}
                  isLoggedIn={isLoggedIn}
                />
              ))}
            </div>
          </>
        )}

        {/* View full stats */}
        <div className="mx-5 mt-4">
          <button
            onClick={() => handleNav("/stats")}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl
                       bg-gray-100 dark:bg-white/[0.04]
                       border border-gray-200 dark:border-white/[0.07]
                       hover:bg-gray-200 dark:hover:bg-white/[0.08]
                       transition-colors text-sm font-semibold
                       text-gray-500 dark:text-gray-400
                       hover:text-gray-800 dark:hover:text-white"
          >
            Full Stats Page
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </DrawerShell>
  );
};

export default StatsDrawer;
